import React, { useState, useEffect } from 'react';
import {
  PhoneIncoming, RefreshCw, Check, X, RotateCcw, Clock, MapPin, Mail, Phone as PhoneIcon,
  Package as PackageIcon, ChevronDown, Gauge, CalendarClock,
} from 'lucide-react';
import {
  getAllInboundRequests, updateInboundRequestStatus,
  type InboundRequest, type InboundRequestStatus,
} from '../api/inboundRequests';

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_ABBR: Record<string, string> = {
  Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun',
};

const hourLabel = (v: string) => {
  const h = parseInt((v || '').split(':')[0] || '0', 10);
  const period = h < 12 ? 'AM' : 'PM';
  const d12 = h % 12 === 0 ? 12 : h % 12;
  return `${d12}:00 ${period}`;
};

const statusStyles: Record<string, { pill: string; dot: string }> = {
  pending: { pill: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  approved: { pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  declined: { pill: 'bg-red-50 text-red-600 border-red-200', dot: 'bg-red-500' },
};

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const InboundInquiries: React.FC = () => {
  const [requests, setRequests] = useState<InboundRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | InboundRequestStatus>('all');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const result = await getAllInboundRequests();
      if (result.success && result.data) setRequests(result.data);
    } catch (error) {
      console.error('Error loading inbound inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (id: string, status: InboundRequestStatus) => {
    setActionLoading(id);
    try {
      const result = await updateInboundRequestStatus(id, status);
      if (result.success) {
        setRequests((prev) =>
          prev.map((r) => (r.id === id ? { ...r, status, reviewed_at: new Date().toISOString() } : r))
        );
      }
    } catch (error) {
      console.error('Error updating inbound inquiry:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const counts = {
    total: requests.length,
    pending: requests.filter((r) => r.status === 'pending').length,
    approved: requests.filter((r) => r.status === 'approved').length,
    declined: requests.filter((r) => r.status === 'declined').length,
  };
  const filtered = statusFilter === 'all' ? requests : requests.filter((r) => r.status === statusFilter);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-navy-600 text-white">
              <PhoneIncoming className="w-4 h-4" />
            </span>
            Inbound Inquiries
          </h2>
          <p className="text-sm text-slate-500 mt-1">FEX inbound &amp; live-transfer requests from agents. Review and approve or decline each one.</p>
        </div>
        <button
          onClick={loadRequests}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors self-start"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Filter tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total', value: counts.total, key: 'all' as const, ring: 'ring-navy-500', text: 'text-slate-900' },
          { label: 'Pending', value: counts.pending, key: 'pending' as const, ring: 'ring-amber-400', text: 'text-amber-600' },
          { label: 'Approved', value: counts.approved, key: 'approved' as const, ring: 'ring-emerald-400', text: 'text-emerald-600' },
          { label: 'Declined', value: counts.declined, key: 'declined' as const, ring: 'ring-red-400', text: 'text-red-600' },
        ].map((s) => (
          <button
            key={s.label}
            onClick={() => setStatusFilter(s.key)}
            className={`text-left bg-white border rounded-xl p-4 transition-all ${statusFilter === s.key ? `border-transparent ring-2 ${s.ring}` : 'border-slate-200 hover:border-slate-300'}`}
          >
            <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.text}`}>{s.value}</p>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-5 h-5 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-xl px-5 py-16 text-center bg-slate-50/50">
          <PhoneIncoming className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No {statusFilter === 'all' ? '' : statusFilter} inbound inquiries{statusFilter === 'all' ? ' yet' : ''}.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const isOpen = expandedId === r.id;
            const busy = actionLoading === r.id;
            const openDays = DAY_ORDER.filter((d) => r.schedule?.[d] && r.schedule[d].mode !== 'closed');
            const st = statusStyles[r.status] || statusStyles.pending;
            return (
              <div key={r.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden transition-shadow hover:shadow-sm">
                {/* Card head */}
                <div className="p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    {/* Left: who + what */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-navy-50 text-navy-700 border border-navy-100">
                          {r.product_name}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${st.pill}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /> {r.status}
                        </span>
                      </div>
                      <p className="text-base font-bold text-slate-900 truncate">{r.full_name}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-slate-500">
                        <span className="inline-flex items-center gap-1.5"><PhoneIcon className="w-3.5 h-3.5 text-slate-400" /> {r.phone}</span>
                        <span className="inline-flex items-center gap-1.5 min-w-0"><Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /> <span className="truncate">{r.email}</span></span>
                      </div>
                    </div>

                    {/* Right: actions */}
                    <div className="flex-shrink-0">
                      {r.status === 'pending' ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleStatus(r.id, 'approved')}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                          >
                            <Check className="w-4 h-4" /> Approve
                          </button>
                          <button
                            onClick={() => handleStatus(r.id, 'declined')}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                          >
                            <X className="w-4 h-4" /> Decline
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleStatus(r.id, 'pending')}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Reset to pending
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Quick facts */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><PackageIcon className="w-3 h-3" /> Package</p>
                      <p className="text-sm font-semibold text-slate-800 mt-0.5">{r.package_size} {r.unit_label}s</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><MapPin className="w-3 h-3" /> States</p>
                      <p className="text-sm font-semibold text-slate-800 mt-0.5">{r.state_count}</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Gauge className="w-3 h-3" /> Daily cap</p>
                      <p className="text-sm font-semibold text-slate-800 mt-0.5">{r.daily_cap || '—'}</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Submitted</p>
                      <p className="text-sm font-semibold text-slate-800 mt-0.5">{fmtDate(r.created_at)}</p>
                    </div>
                  </div>

                  {/* Expand toggle */}
                  <button
                    onClick={() => setExpandedId(isOpen ? null : r.id)}
                    className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-navy-600 hover:text-navy-700 transition-colors"
                  >
                    {isOpen ? 'Hide' : 'View'} schedule, states &amp; instructions
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {/* Expanded details */}
                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/60 p-4 sm:p-5">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> States ({r.state_count})</p>
                        <p className="text-sm text-slate-600 leading-relaxed">{r.states || '—'}</p>
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 mt-4">Pricing</p>
                        <p className="text-sm text-slate-600">${r.price_per_unit}/{r.unit_label} · {r.package_size} {r.unit_label}s</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Schedule</p>
                        {openDays.length === 0 ? (
                          <p className="text-sm text-slate-400">No days set</p>
                        ) : (
                          <ul className="space-y-0.5">
                            {openDays.map((d) => (
                              <li key={d} className="text-sm text-slate-600">
                                <span className="font-medium text-slate-700 inline-block w-9">{DAY_ABBR[d]}:</span>{' '}
                                {r.schedule[d].mode === 'all-day' ? 'Open all day' : `${hourLabel(r.schedule[d].start)} – ${hourLabel(r.schedule[d].end)}`}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    {r.special_instructions && (
                      <div className="mt-5 pt-4 border-t border-slate-200">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Special instructions</p>
                        <p className="text-sm text-slate-600 leading-relaxed">{r.special_instructions}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InboundInquiries;
